/**
 * Printer auto power-off and BLE keep-alive preferences (per browser).
 */
import { clampXprinterAutoPowerOffMinutes } from './escpos/xprinterVendorCommands';

const LS_KEY = 'zeettu.printer.powerSettings';

export const BLE_KEEP_ALIVE_INTERVAL_SEC = 45;

export type PrinterPowerSettings = {
  /** Minutes until auto shutdown; 0 = never. Max 120. */
  autoPowerOffMinutes: number;
  /** Send set command after every successful connect. */
  applyOnConnect: boolean;
  /** Periodic BLE wake while tab is visible. */
  bleKeepAliveEnabled: boolean;
};

type PowerSettingsListener = () => void;
const listeners = new Set<PowerSettingsListener>();

export function subscribePrinterPowerSettings(listener: PowerSettingsListener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function notifyPowerSettingsChange(): void {
  for (const fn of listeners) {
    try {
      fn();
    } catch {
      /* ignore */
    }
  }
}

export function getDefaultPrinterPowerSettings(): PrinterPowerSettings {
  return {
    autoPowerOffMinutes: 120,
    applyOnConnect: true,
    bleKeepAliveEnabled: true,
  };
}

function normalizeSettings(raw: Partial<PrinterPowerSettings> | null | undefined): PrinterPowerSettings {
  const defaults = getDefaultPrinterPowerSettings();
  if (!raw) return defaults;
  return {
    autoPowerOffMinutes:
      typeof raw.autoPowerOffMinutes === 'number'
        ? clampXprinterAutoPowerOffMinutes(raw.autoPowerOffMinutes)
        : defaults.autoPowerOffMinutes,
    applyOnConnect: typeof raw.applyOnConnect === 'boolean' ? raw.applyOnConnect : defaults.applyOnConnect,
    bleKeepAliveEnabled:
      typeof raw.bleKeepAliveEnabled === 'boolean' ? raw.bleKeepAliveEnabled : defaults.bleKeepAliveEnabled,
  };
}

function readStored(): PrinterPowerSettings {
  const defaults = getDefaultPrinterPowerSettings();
  if (typeof globalThis === 'undefined' || !('localStorage' in globalThis)) {
    return defaults;
  }
  try {
    const raw = globalThis.localStorage.getItem(LS_KEY);
    if (!raw?.trim()) return defaults;
    const j = JSON.parse(raw) as Partial<PrinterPowerSettings>;
    return normalizeSettings(j);
  } catch {
    return defaults;
  }
}

export function getPrinterPowerSettings(): PrinterPowerSettings {
  return readStored();
}

export function savePrinterPowerSettings(next: PrinterPowerSettings): void {
  const normalized = normalizeSettings(next);
  if (typeof globalThis === 'undefined' || !('localStorage' in globalThis)) return;
  try {
    globalThis.localStorage.setItem(LS_KEY, JSON.stringify(normalized));
    notifyPowerSettingsChange();
  } catch {
    /* quota */
  }
}

export function clearPrinterPowerSettings(): void {
  if (typeof globalThis === 'undefined' || !('localStorage' in globalThis)) return;
  try {
    globalThis.localStorage.removeItem(LS_KEY);
    notifyPowerSettingsChange();
  } catch {
    /* ignore */
  }
}

export const AUTO_POWER_OFF_PRESET_MINUTES = [0, 15, 30, 60, 90, 120] as const;
